'use client';
import React, { useEffect, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

interface ExperienceLevel {
  level: string;
  badge: string;
  reviews: string;
  earningRange: string;
  color: string;
  bgColor: string;
  perks: string[];
}

const DEFAULT_LEVELS: ExperienceLevel[] = [
  { level: 'Newcomer', badge: '🌱', reviews: '0–5 reviews', earningRange: '₹400–₹700', color: '#6B7280', bgColor: '#F8F9FA', perks: ['Access to entry-level campaigns', 'Basic product categories', 'Email support'] },
  { level: 'Explorer', badge: '⭐', reviews: '6–20 reviews', earningRange: '₹700–₹1,200', color: '#1565C0', bgColor: '#E3F2FD', perks: ['More campaign variety', 'Priority application queue', 'Electronics & Beauty access'] },
  { level: 'Verified', badge: '✅', reviews: '21–50 reviews', earningRange: '₹1,200–₹2,000', color: '#FF6B35', bgColor: '#FFE0D0', perks: ['Premium brand campaigns', 'Higher reward campaigns', 'Dedicated support'] },
  { level: 'Pro', badge: '🏆', reviews: '51–100 reviews', earningRange: '₹2,000–₹3,500', color: '#7B1FA2', bgColor: '#F3E5F5', perks: ['Exclusive high-value campaigns', 'Creator slot eligibility', 'Early access to new brands'] },
  { level: 'Elite', badge: '💎', reviews: '100+ reviews', earningRange: '₹3,500–₹5,000+', color: '#FFB300', bgColor: '#FFF8E1', perks: ['Top-tier brand partnerships', 'Influencer campaign access', 'Monthly bonus rewards'] },
];

export default function ExperienceLevels() {
  const [levels, setLevels] = useState<ExperienceLevel[]>(DEFAULT_LEVELS);

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('platform_settings')
      .select('value')
      .eq('key', 'experience_levels')
      .maybeSingle()
      .then(({ data }) => {
        if (data?.value) {
          try { setLevels(JSON.parse(data.value)); } catch {}
        }
      });
  }, []);

  return (
    <section className="section-py bg-surface-variant">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-semibold uppercase tracking-widest text-primary bg-primary-container px-3 py-1 rounded-full mb-3">
            Experience Levels
          </span>
          <h2 className="font-heading font-bold text-foreground" style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
            Grow Your Reviewer Level
          </h2>
          <p className="text-muted-foreground mt-2 text-sm max-w-md mx-auto">
            The more you review, the higher your level — and the better the campaigns you unlock.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {levels.map((lvl) => (
            <div
              key={lvl.level}
              className="bg-card rounded-2xl border border-border p-5 flex flex-col space-y-4 card-hover"
            >
              <div className="text-3xl">{lvl.badge}</div>
              <div>
                <div className="font-heading font-bold text-foreground">{lvl.level}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{lvl.reviews}</div>
              </div>
              <div
                className="inline-block text-xs font-bold px-2.5 py-1 rounded-full w-fit"
                style={{ backgroundColor: lvl.bgColor, color: lvl.color }}
              >
                {lvl.earningRange}/month
              </div>
              <ul className="space-y-1.5 flex-1">
                {lvl.perks.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-xs text-muted-foreground">
                    <Icon name="CheckCircleIcon" size={12} className="shrink-0 mt-0.5" style={{ color: lvl.color }} />
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}